import { useState } from "react";
import { fetchClients } from "../api/clientsApi";
import { plantNameParts } from "../utils/plantNameHelpers";

/**
 * "Add / Modify / Delete Project" (a plant - the tree's top level) dialogs.
 *
 * Wraps the data layer's create/update/delete calls with the form state,
 * validation messages and saving flags each dialog needs.
 */
export const usePlantOperations = ({
  createPlant,
  updatePlant,
  deletePlant,
  wbsByPlant,
}) => {
  const [addPlantOpen, setAddPlantOpen] = useState(false);
  const [plantForm, setPlantForm] = useState({
    clientId: "",
    namePrefix: "",
    nameNumber: "",
    description: "",
    sharepointLink: "",
  });
  const [savingPlant, setSavingPlant] = useState(false);
  const [plantError, setPlantError] = useState("");
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  // Right-click on a project (plant) row: where the menu opens and which
  // project it acts on (null = no menu open).
  const [plantMenu, setPlantMenu] = useState(null);

  // Project being renamed / redescribed (null = dialog closed). `number` is
  // the half of the name after the client prefix - the same split the Add
  // Project dialog composes a new name from.
  const [editPlantTarget, setEditPlantTarget] = useState(null);
  const [editPlantForm, setEditPlantForm] = useState({
    number: "",
    description: "",
  });
  const [savingPlantEdit, setSavingPlantEdit] = useState(false);
  const [editPlantError, setEditPlantError] = useState("");

  // Project pending deletion (null = dialog closed).
  const [deletePlantTarget, setDeletePlantTarget] = useState(null);
  const [deletingPlant, setDeletingPlant] = useState(false);
  const [deletePlantError, setDeletePlantError] = useState("");

  const openAddPlant = () => {
    setPlantForm({
      clientId: "",
      namePrefix: "",
      nameNumber: "",
      description: "",
      sharepointLink: "",
    });
    setPlantError("");
    setAddPlantOpen(true);

    if (clients.length || clientsLoading) return;
    setClientsLoading(true);
    fetchClients()
      .then(setClients)
      .finally(() => setClientsLoading(false));
  };

  const handleCreatePlant = async () => {
    if (!plantForm.clientId) {
      setPlantError("Client is required");
      return;
    }
    if (!plantForm.nameNumber.trim()) {
      setPlantError("Number is required");
      return;
    }
    setSavingPlant(true);
    setPlantError("");
    try {
      await createPlant({
        ...plantForm,
        nameNumber: plantForm.nameNumber.trim(),
      });
      setAddPlantOpen(false);
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to create project";
      setPlantError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setSavingPlant(false);
    }
  };

  // Right-click anywhere on a project row - same behaviour as a WBS row's.
  const openPlantMenu = (event, plant) => {
    event.preventDefault();
    event.stopPropagation();
    setPlantMenu({ mouseX: event.clientX, mouseY: event.clientY, plant });
  };

  const closePlantMenu = () => setPlantMenu(null);

  const openEditPlant = (plant) => {
    setEditPlantTarget(plant);
    setEditPlantForm({
      number: plantNameParts(plant).number,
      description: plant?.description || "",
    });
    setEditPlantError("");
    closePlantMenu();
  };

  const handleUpdatePlant = async () => {
    const number = editPlantForm.number.trim();
    if (!number) {
      setEditPlantError("Number is required");
      return;
    }

    const { prefix } = plantNameParts(editPlantTarget);

    setSavingPlantEdit(true);
    setEditPlantError("");
    try {
      await updatePlant(editPlantTarget.id, {
        // Composed the same way the Add Project dialog composes a new one,
        // so an edit can't take the name outside its client's scheme.
        name: [prefix, number].filter(Boolean).join("-"),
        description: editPlantForm.description,
      });
      setEditPlantTarget(null);
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to update project";
      setEditPlantError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setSavingPlantEdit(false);
    }
  };

  const openDeletePlant = (plant) => {
    setDeletePlantTarget(plant);
    setDeletePlantError("");
    closePlantMenu();
  };

  const handleDeletePlant = async () => {
    setDeletingPlant(true);
    setDeletePlantError("");
    try {
      await deletePlant(deletePlantTarget.id);
      setDeletePlantTarget(null);
    } catch (err) {
      // Typically the database refusing while another register (MRQs,
      // change orders, tasks) still points at this project.
      const message =
        err?.response?.data?.message || "Failed to delete project";
      setDeletePlantError(
        Array.isArray(message) ? message.join(", ") : message,
      );
    } finally {
      setDeletingPlant(false);
    }
  };

  return {
    wbsByPlant,
    addPlantOpen,
    setAddPlantOpen,
    plantForm,
    setPlantForm,
    savingPlant,
    plantError,
    clients,
    clientsLoading,
    openAddPlant,
    handleCreatePlant,
    plantMenu,
    openPlantMenu,
    closePlantMenu,
    editPlantTarget,
    setEditPlantTarget,
    editPlantForm,
    setEditPlantForm,
    savingPlantEdit,
    editPlantError,
    openEditPlant,
    handleUpdatePlant,
    deletePlantTarget,
    setDeletePlantTarget,
    deletingPlant,
    deletePlantError,
    openDeletePlant,
    handleDeletePlant,
  };
};
